import EventEmitter from 'events';
import { useCallback, useEffect, useRef, useState } from 'react';
import Video, { ConnectOptions, LocalTrack, Room } from 'twilio-video';
import { Callback } from '../../../types';
import logMessage from '../../ErrorDialog/logMessage';

export default function useRoom(localTracks: LocalTrack[], onError: Callback, options?: ConnectOptions) {
  const [room, setRoom] = useState<Room>(new EventEmitter() as Room);
  const [isConnecting, setIsConnecting] = useState(false);
  const disconnectHandlerRef = useRef<() => void>(() => {});
  const localTracksRef = useRef<LocalTrack[]>([]);

  useEffect(() => {
    // It can take a moment for Video.connect to connect to a room. During this time, the user may have enabled or disabled their
    // local audio or video tracks. If this happens, we store the localTracks in this ref, so that they are correctly published
    // once the user is connected to the room.
    localTracksRef.current = localTracks;
  }, [localTracks]);

  const connect = useCallback(
    token => {
      setIsConnecting(true);
      return Video.connect(token, { ...options, tracks: [] }).then(
        newRoom => {
          setRoom(newRoom);

          newRoom.once('disconnected', () => {
            // Reset the room only after all other `disconnected` listeners have been called.
            setTimeout(() => setRoom(new EventEmitter() as Room));
            window.removeEventListener('beforeunload', disconnectHandlerRef.current);

            //@ts-ignore
            if (window.intervalPing !== undefined) {
              //@ts-ignore
              clearInterval(window.intervalPing);
            }
          });

          // @ts-ignore
          window.twilioRoom = newRoom;

          localTracksRef.current.forEach(track =>
            // Tracks can be supplied as arguments to the Video.connect() function and they will automatically be published.
            // However, tracks must be published manually in order to set the priority on them.
            // All video tracks are published with 'low' priority. This works because the video
            // track that is displayed in the 'MainParticipant' component will have it's priority
            // set to 'high' via track.setPriority()
            newRoom.localParticipant.publishTrack(track, { priority: track.kind === 'video' ? 'low' : 'standard' })
          );

          disconnectHandlerRef.current = () => newRoom.disconnect();
          setIsConnecting(false);

          if (process.env.REACT_APP_KEEP_ALIVE !== undefined) {
            console.log(process.env.REACT_APP_KEEP_ALIVE);
            console.log(process.env.REACT_APP_KEEP_ALIVE_MAX);

            const interval: number = parseInt(process.env.REACT_APP_KEEP_ALIVE) * 1000;
            const w: any = window;
            let counter = 0;

            w.intervalPing = setInterval(() => {
              if (process.env.REACT_APP_KEEP_ALIVE_MAX) {
                counter++;
                logMessage('KeepAlive', -9999);

                const max: number = parseInt(process.env.REACT_APP_KEEP_ALIVE_MAX);
                if (counter >= max && max > 0) {
                  console.log('Maximum keep-alive reached');
                  clearInterval(w.intervalPing);
                }
              }
            }, interval);
          }

          // Add a listener to disconnect from the room when a user closes their browser
          window.addEventListener('beforeunload', disconnectHandlerRef.current);
        },
        error => {
          onError(error);
          setIsConnecting(false);
        }
      );
    },
    [options, onError]
  );

  return { room, isConnecting, connect };
}
